import { FloatingPortal } from "@floating-ui/react"
import { Button } from "../../../Button"

import { noop } from "lodash-es"
import { ComponentProps, MouseEventHandler, cloneElement, useRef, useState } from "react"
import { Checkbox } from "../../../Checkbox"
import { FormLabel } from "../../../FormLabel"
import { Icon } from "../../../Icon"
import { Popover } from "../../../Popover"
import { TableRowSelectOptionList } from "../TableRowSelectOptionList"
import { useTableContext } from "../../TableContext"
import { MobileSelectMenuProps, TableBulkActionsBarProps } from "./types"

const MobileSelectMenu = ({
  ariaLabel = "Selection menu",
  onSelectOption,
}: MobileSelectMenuProps) => {
  const { selectAllCheckboxState, selectLabel, multiRowSelectOptionsConfig } = useTableContext()
  const [isOpen, setIsOpen] = useState(false)
  const onRequestClose = () => setIsOpen(false)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} placement="top-end">
      <Popover.Trigger>
        <button
          className="flex items-center gap-2 outline-black focus-visible:outline"
          type="button"
          aria-label={ariaLabel}
          onClick={() => setIsOpen(true)}
        >
          <Checkbox
            // We are purely using the checkbox as a decorating element here. So hide its semantics from the accessibility tree
            aria-hidden
            checked={selectAllCheckboxState === "CHECKED"}
            isIndeterminate={selectAllCheckboxState === "INDETERMINATE"}
            onChange={noop} // Suppress warnings
            className="pointer-events-none"
            label={<FormLabel label="Select all users" className="sr-only" />}
            tabIndex={-1}
            value="all"
          />
          <span className="whitespace-nowrap">{selectLabel}</span>
          <Icon size="xs" name="ChevronUp" aria-hidden />
        </button>
      </Popover.Trigger>
      <FloatingPortal>
        <Popover.Content className="rounded-md border border-gray-200 shadow-md">
          <TableRowSelectOptionList
            onRequestClose={onRequestClose}
            onSelect={onSelectOption}
            multiRowSelectOptionsConfig={multiRowSelectOptionsConfig}
          />
        </Popover.Content>
      </FloatingPortal>
    </Popover>
  )
}

export const TableBulkActionsBar = ({
  infoText,
  isOpen,
  numberOfSelectedRecords,
  customSelectedRecordsMessage,
  onSelectOption,
  primaryActionButton,
  clearSelectionButton,
}: TableBulkActionsBarProps) => {
  const isControlled = useRef(typeof isOpen !== "undefined")
  const hasRendered = useRef(false)

  // By default, the bulk action bar returns null, shows when numberOfSelectedRecords > 1, stays showing while numberOfSelectedRecords < 0, then hides when the bulk action button is clicked
  if (isControlled.current ? !isOpen : numberOfSelectedRecords < 1 && !hasRendered.current) {
    return null
  }
  hasRendered.current = true

  const primaryActionButtonProps: ComponentProps<typeof Button> = {
    ...primaryActionButton.props,
    variation: "primary",
    disabled: numberOfSelectedRecords < 1,
    className: "w-full md:w-fit",
    onClick: (e => {
      hasRendered.current = false
      primaryActionButton.props.onClick?.(e)
    }) as MouseEventHandler<HTMLButtonElement>,
  }
  const _primaryActionButton = cloneElement(primaryActionButton, primaryActionButtonProps)

  const clearSelectionButtonProps: ComponentProps<typeof Button> = {
    ...clearSelectionButton.props,
    variation: "borderless",
    disabled: numberOfSelectedRecords < 1,
    className: "hidden md:ml-auto md:block",
  }
  const _clearSelectionButton = cloneElement(clearSelectionButton, clearSelectionButtonProps)

  const numberOfRecordsSelectedMessage: string =
    typeof customSelectedRecordsMessage === "undefined"
      ? `${numberOfSelectedRecords} records selected`
      : customSelectedRecordsMessage

  return (
    <div className="sticky bottom-2 mt-4 flex flex-col gap-5 overflow-visible rounded-md border border-gray-100 bg-white px-6 py-4 shadow-md md:flex-row">
      <div className="flex justify-between gap-2  md:flex-col md:items-center md:justify-center md:gap-0">
        <span className="w-full md:font-semibold">{numberOfRecordsSelectedMessage}</span>
        {infoText && <span className="hidden text-gray-400 md:block">{infoText}</span>}
        <div className="md:hidden">
          <MobileSelectMenu onSelectOption={onSelectOption} />
        </div>
      </div>
      {_clearSelectionButton}
      {_primaryActionButton}
    </div>
  )
}

TableBulkActionsBar.displayName = "Table.BulkActionsBar"
